import { useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { showErrorToast, tagsEnabled } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import {
  Badge,
  Column,
  ConfirmationModal,
  MenuComponent,
  ModalLoading,
  ModalSuccess,
  TableServer,
  UserEditModal,
} from '@Components';
import { ClientObject, Options, UserType } from '@Models';
import { bopsApi } from '@Network';
import { AppDispatch, RootState, tableServerRevision, usersOperations } from '@Store';
import config from '../../config';
import UserEditPermissionsModal from '../UserModals/UserEditPermissionsModal';
import UserEditTagsModal from '../UserModals/UserEditTagsModal';

const UserList = ({
  usersData,
  clientsObjects,
  total,
  client,
  emailQuery,
  pageSize,
  setTotal,
  IncrementEvent,
  fetchUsers,
  clients,
}: {
  usersData: UserType[];
  clientsObjects: ClientObject;
  total: number;
  client: string;
  emailQuery: string;
  pageSize: number;
  IncrementEvent: (event: string) => void;
  fetchUsers: () => void;
  setTotal: (total: number) => void;
  clients: Options[];
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [email, setEmail] = useState('');
  const [clientId, setClientId] = useState<string>('');
  const [oldTags, setOldTags] = useState<string[]>([]);
  const [blocked, setBlocked] = useState(false);
  const [, setIndex] = useState(0);
  const [changePassword, setChangePassword] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEditTags, setOpenEditTags] = useState(false);
  const [openEditPermissions, setOpenEditPermissions] = useState(false);
  const changePasswordUser = () => {
    setIsModalOpen(false);
    setOpenLoading(true);
    bopsApi
      .changePasswordUser(email)
      .then(() => {
        setOpenSuccess(true);
        setOpenLoading(false);
        setChangePassword(false);
      })
      .catch((error) => {
        sendErrorEvent(error);
        showErrorToast(t('User.ErrorChangePassword'));
        setOpenLoading(false);
        setChangePassword(false);
      });
  };

  const blockorUnblockUser = () => {
    setIsModalOpen(false);
    bopsApi
      .blockUnblockUser({ blocked: !blocked, email, clientId, tags: oldTags })
      .then(() => {
        IncrementEvent('usersEvent');
      })
      .catch((error) => {
        sendErrorEvent(error);
        showErrorToast(t('User.ErrorBlock'));
      });
  };

  const deleteUser = () => {
    setIsModalOpen(false);
    setOpenLoading(true);
    bopsApi
      .deleteUser({ email, clientId, tags: oldTags })
      .then(() => {
        IncrementEvent('usersEvent');
        setOpenLoading(false);
        setOpenDelete(false);
      })
      .catch((error) => {
        sendErrorEvent(error);
        showErrorToast(t('User.ErrorDelete'));
        setOpenLoading(false);
        setOpenDelete(false);
      });
  };

  const getValueCell = (row, key: string): string | any[] => {
    let cellValue = row?.original[key];
    if (clientsObjects) {
      if (key === 'client_id') {
        if (Array.isArray(cellValue)) {
          return cellValue.map((keyId: { id: string }) => clientsObjects?.[keyId.id] ?? keyId.id);
        }
        // A user can still point at a client the options list no longer
        // carries (a deactivated one), so fall back to the raw id instead
        // of an empty cell — same as the array branch above.
        cellValue = clientsObjects?.[cellValue] ?? cellValue;
      }
      if (key === 'tags' && clientsObjects) {
        const tags = row?.original?.app_metadata?.tags;
        if (Array.isArray(tags)) {
          return tags.map((tag: string) => (tag.length > 20 ? `${tag.slice(0, 20)}...` : tag));
        }
      }
    }
    if (key === 'last_login') cellValue = cellValue ? moment(cellValue).format('l') : t('User.Never');
    return cellValue;
  };

  const builderCell = (
    type: string,
    label: string,
    value,
    id: string,
    emailUser: string,
    _clientId: string,
    tags: string[],
    block: boolean,
  ): JSX.Element => {
    if (type === 'string' || (type === 'badge' && !value)) return <div key={label}>{value}</div>;

    if (type === 'badge' && value) {
      if (Array.isArray(value)) {
        return (
          <div className="flex gap-2 flex-wrap">
            {value.map((item: string, key: number) => (
              <Badge
                variant="gray"
                label={item.length > 20 ? `${item.slice(0, 20)}...` : item}
                key={item}
                tooltipId={`${key} ${item}`}
                toolInfo={item.length > 20 ? item : undefined}
              />
            ))}
          </div>
        );
      }
      return <Badge variant="gray" label={value} />;
    }

    if (type === 'badge-status') {
      return <Badge variant={value ? 'gray' : 'green'} label={value ? t('User.Blocked') : t('User.Active')} showDot />;
    }
    const menuOptions = [
      {
        id: 'block',
        label: block ? t('User.UnBlockLabel') : t('User.BlockLabel'),
        onAction: () => {
          setIsModalOpen(true);
          setBlocked(block);
          setIndex(+id);
          setEmail(emailUser);
          setClientId(_clientId);
          setOldTags(tags);
        },
      },
      {
        id: 'changeAccount',
        label: t('User.ChangeAccount'),
        onAction: () => {
          setOpenEdit(true);
          setEmail(emailUser);
          setClientId(_clientId);
          setOldTags(tags);
        },
      },
      {
        id: 'editTags',
        label: t('User.EditTags'),
        onAction: () => {
          if (!_clientId) showErrorToast(t('User.ErrorClientId'));
          else {
            setOpenEditTags(true);
            setEmail(emailUser);
            setClientId(_clientId);
            setOldTags(tags);
          }
        },
      },
      {
        id: 'editPermissions',
        label: t('User.EditPermissions'),
        onAction: () => {
          setOpenEditPermissions(true);
          setEmail(emailUser);
        },
      },
      {
        id: 'changePassword',
        label: t('User.ChangePassword'),
        onAction: () => {
          setIsModalOpen(true);
          setChangePassword(true);
          setEmail(emailUser);
        },
      },
      {
        id: 'delete',
        label: t('User.Delete'),
        onAction: () => {
          setIsModalOpen(true);
          setEmail(emailUser);
          setOpenDelete(true);
          setClientId(_clientId);
          setOldTags(tags);
        },
      },
    ];

    // Two independent rules, both listing what to REMOVE: gs1 has no per-user
    // permissions, and the tags action is dropped wherever tags are not the
    // access mechanism.
    const hiddenOptionIds = [...(config.isGs1Flavor ? ['editPermissions'] : []), ...(tagsEnabled ? [] : ['editTags'])];

    const filteredOptions = menuOptions.filter((option) => !hiddenOptionIds.includes(option.id));

    return <MenuComponent className="cursor-pointer" options={filteredOptions} />;
  };

  const filterQuery = useMemo(() => {
    return `email=${emailQuery}&client=${client}`;
  }, [emailQuery, client]);

  const getColumns = (): Column[] => {
    // Each set sums to 100%. Dropping the Tags column frees 35%, which goes to
    // the columns holding variable-length text; the status badge and the
    // actions button render fixed-width content, so they keep their 10%.
    const width = tagsEnabled
      ? { email: 'w-[20%]', client: 'w-[15%]', lastLogin: 'w-[10%]' }
      : { email: 'w-[40%]', client: 'w-[25%]', lastLogin: 'w-[15%]' };

    const columns = [
      { label: t('User.Email'), accessor: 'email', key: 'email', type: 'string', width: width.email },
      {
        label: t('User.Client'),
        accessor: 'client_id',
        key: 'client_id',
        type: 'badge',
        width: width.client,
      },
      ...(tagsEnabled
        ? [
            {
              label: t('User.Tags'),
              accessor: 'tags',
              key: 'tags',
              type: 'badge',
              width: 'w-[35%]',
            },
          ]
        : []),
      {
        label: t('User.LastLogin'),
        accessor: 'last_login',
        key: 'last_login',
        type: 'string',
        width: width.lastLogin,
      },
      {
        label: t('User.Status'),
        accessor: 'blocked',
        key: 'blocked',
        type: 'badge-status',
        width: 'w-[10%]',
      },
      { label: '', accessor: 'block', key: 'status', type: 'button', width: 'w-[10%]' },
    ];

    const headers = columns.map((column) => {
      return {
        Header: column.label,
        accessor: column.accessor,
        Cell: ({ row }) => {
          const cellValue = getValueCell(row, column.key);
          return builderCell(
            column.type,
            column.label,
            cellValue,
            row.id,
            row?.original?.email,
            row?.original?.client_id,
            row?.original?.tags,
            row?.original?.blocked,
          );
        },
        width: column.width,
      };
    });
    return headers;
  };

  return (
    <>
      <TableServer
        sortable
        columns={getColumns()}
        dataResult={usersData}
        pageSize={pageSize}
        pagination
        total={total}
        setTotal={setTotal}
        filters={filterQuery}
        startPagination={0}
        endpoint="getUsers"
        indexPagination="users"
        incrementName="usersEvent"
        disableSortFields={['app_metadata.clientId']}
      />

      <ConfirmationModal
        buttonNamePrefix="USER_LIST"
        confirmText={
          changePassword
            ? `${t('User.ConfirmChangePassword') + email}?`
            : openDelete
            ? `${t('User.DeleteConfirm') + email}?`
            : blocked
            ? `${t('User.ConfirmUnBlock') + email}?`
            : `${t('User.ConfirmBlock') + email}?`
        }
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setChangePassword(false);
        }}
        onConfirm={changePassword ? changePasswordUser : openDelete ? deleteUser : blockorUnblockUser}
      />

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center">
          <span className="text-center">
            {openDelete ? `${t('User.LoadingDelete')} ${email}` : `${t('User.LoadingChangePassword')} ${email}`}
          </span>
        </div>
      </ModalLoading>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center">
          <span className="text-center"> {t('User.EmailSent') + email} </span>
        </div>
      </ModalSuccess>

      <UserEditModal
        isOpen={openEdit}
        currentClient={clientId}
        onClose={() => setOpenEdit(false)}
        fetchUsers={fetchUsers}
        email={email}
        clients={clients}
        clientValue={clientId ? { value: clientId, label: clientsObjects?.[clientId] ?? clientId } : undefined}
        currentTags={oldTags}
      />

      {tagsEnabled && (
        <UserEditTagsModal
          isOpen={openEditTags}
          onClose={() => setOpenEditTags(false)}
          fetchUsers={fetchUsers}
          email={email}
          clientValue={clientId ? { value: clientId, label: clientsObjects?.[clientId] ?? clientId } : undefined}
          oldTags={oldTags}
        />
      )}

      <UserEditPermissionsModal
        isOpen={openEditPermissions}
        onClose={() => setOpenEditPermissions(false)}
        fetchUsers={fetchUsers}
        email={email}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { userList } = state;
  const { users } = userList;
  return { users };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { fetchUsers } = usersOperations;
  const { IncrementEvent } = tableServerRevision;

  return {
    fetchUsers: () => dispatch(fetchUsers()),
    IncrementEvent: (event: string) => dispatch(IncrementEvent(event)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserList);
